/**
 * /app/salon/split-check — Dividir la cuenta de una mesa entre comensales.
 * Sirve al cobrar: repartir el total en partes iguales o montos custom
 * (no toma el pedido; eso es "Pedido" / comanda).
 */
import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { ClinicalPageShell } from "@/components/clinical";
import { Button } from "@/components/ui/button";
import { UtensilsCrossed, ArrowLeft, CheckCircle2 } from "lucide-react";
import { CrudShellHeader, Loading } from "@/components/ds";
import { apiGetJson, apiSendJson } from "@/lib/sm-api";
import { useWorkspaceTenantId } from "@/hooks/use-workspace-tenant-id";
import { toast } from "sonner";

export const Route = createFileRoute("/app/salon/split-check")({
  component: SplitCheckPage,
  validateSearch: (s: Record<string, unknown>) => ({
    tableId: String(s.tableId ?? ""),
    sessionId: String(s.sessionId ?? ""),
  }),
  head: () => ({ meta: [{ title: "Dividir cuenta — Salón" }] }),
});

const pesos = (n: number) =>
  new Intl.NumberFormat("es-AR", { style: "currency", currency: "ARS", maximumFractionDigits: 0 }).format(n);

function SplitCheckPage() {
  const { tableId, sessionId: sessionIdParam } = Route.useSearch();
  const tid = useWorkspaceTenantId();
  const [session, setSession] = useState<{
    id: string;
    totalAcumulado: number;
    comensales: number;
    tableName?: string;
  } | null>(null);
  const [partes, setPartes] = useState(2);
  const [mode, setMode] = useState<"igual" | "custom">("igual");
  const [customAmounts, setCustomAmounts] = useState<string[]>(["", ""]);
  const [loading, setLoading] = useState(true);
  const [procesando, setProcesando] = useState(false);
  const [emptyReason, setEmptyReason] = useState<string | null>(null);

  useEffect(() => {
    if (!tid || !tableId) {
      setLoading(false);
      setEmptyReason(!tableId ? "Falta la mesa. Volvé al salón y abrí Dividir cuenta desde una mesa ocupada." : null);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      setEmptyReason(null);
      try {
        const r = await apiGetJson<{
          session?: {
            id: string;
            total_acumulado?: number;
            totalAcumulado?: number;
            comensales?: number;
          } | null;
          table?: { nombre?: string };
        }>(`/api/table-sessions?tableId=${encodeURIComponent(tableId)}`, tid);

        if (cancelled) return;

        const s = r.session;
        if (!s?.id) {
          setSession(null);
          setEmptyReason("Esta mesa no tiene comanda abierta. Abrí la mesa o tomá un pedido antes de dividir la cuenta.");
          return;
        }
        if (sessionIdParam && sessionIdParam !== s.id) {
          // Prefer live open session from API
        }
        const total = Number(s.total_acumulado ?? s.totalAcumulado ?? 0);
        const comensales = Number(s.comensales ?? 2) || 2;
        setSession({
          id: s.id,
          totalAcumulado: total,
          comensales,
          tableName: r.table?.nombre,
        });
        setPartes(comensales);
        setCustomAmounts(Array(comensales).fill(""));
      } catch {
        if (!cancelled) {
          toast.error("Error al cargar la cuenta de la mesa");
          setEmptyReason("No se pudo cargar la sesión de la mesa.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tid, tableId, sessionIdParam]);

  const total = session?.totalAcumulado ?? 0;
  const porParte = mode === "igual" && partes > 0 ? total / partes : 0;
  const customTotal = customAmounts.reduce((s, v) => s + (parseFloat(v) || 0), 0);
  const diferencia = mode === "custom" ? total - customTotal : 0;

  const handlePartes = (n: number) => {
    setPartes(n);
    setCustomAmounts(Array(n).fill(""));
  };

  const handleConfirmar = async () => {
    if (!tid || !session) return;
    if (total <= 0) {
      toast.error("No hay monto para dividir. Primero cargá pedidos en la mesa.");
      return;
    }
    if (mode === "custom" && Math.abs(diferencia) > 1) {
      toast.error(`La suma de las partes (${pesos(customTotal)}) no coincide con el total (${pesos(total)})`);
      return;
    }
    setProcesando(true);
    try {
      const partesFinal =
        mode === "igual" ? Array(partes).fill(porParte) : customAmounts.map((v) => parseFloat(v) || 0);

      await apiSendJson(
        `/api/table-sessions`,
        "POST",
        {
          action: "split-check",
          sessionId: session.id,
          partes: partesFinal,
        },
        tid,
      );
      toast.success("Cuenta dividida — usá estos montos al cobrar");
      window.location.href = "/app/salon";
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Error al dividir la cuenta");
    } finally {
      setProcesando(false);
    }
  };

  if (loading) {
    return (
      <div>
        <CrudShellHeader title="Dividir cuenta" subtitle="Repartí el total entre comensales al momento de cobrar" />
        <Loading variant="list" rows={3} />
      </div>
    );
  }

  return (
    <div>
      <CrudShellHeader
        title="Dividir cuenta"
        subtitle={
          session
            ? `${session.tableName ? session.tableName + " · " : ""}Total acumulado: ${pesos(total)}`
            : "Repartí el total entre comensales al momento de cobrar"
        }
      />
      <ClinicalPageShell offset="none">
        <div className="mb-4">
          <Link
            to="/app/salon"
            search={{} as never}
            className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> Salón
          </Link>
        </div>

        <div className="mb-4 rounded-xl border border-border/60 bg-card/60 px-4 py-3 text-sm text-muted-foreground">
          <p className="font-medium text-foreground">¿Para qué sirve?</p>
          <p className="mt-1 text-xs leading-relaxed">
            Calcula cuánto paga cada comensal (partes iguales o montos a medida). No toma pedidos:
            para eso usá <span className="text-foreground">Pedido</span> en la mesa. Confirmá la
            división cuando vayas a cobrar.
          </p>
        </div>

        {emptyReason || !session ? (
          <div className="space-y-4 rounded-xl border border-amber-500/30 bg-amber-500/10 px-4 py-6 text-center">
            <p className="text-sm text-amber-200">{emptyReason ?? "Sin sesión de mesa."}</p>
            <Button asChild variant="outline" size="sm">
              <Link to="/app/salon" search={{} as never}>Volver al salón</Link>
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex gap-2">
              {(["igual", "custom"] as const).map((m) => (
                <button
                  key={m}
                  type="button"
                  onClick={() => setMode(m)}
                  className={`rounded-xl border px-4 py-2 text-sm font-medium transition-colors ${
                    mode === m
                      ? "border-primary bg-primary/10 text-primary"
                      : "border-border/60 text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {m === "igual" ? "Partes iguales" : "Montos personalizados"}
                </button>
              ))}
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <p className="text-sm text-muted-foreground">Cantidad de comensales:</p>
              <div className="flex flex-wrap gap-1.5">
                {[2, 3, 4, 5, 6, 8].map((n) => (
                  <button
                    key={n}
                    type="button"
                    onClick={() => handlePartes(n)}
                    className={`flex h-10 w-10 items-center justify-center rounded-xl border text-sm font-semibold transition-colors ${
                      partes === n
                        ? "border-primary bg-primary/20 text-primary"
                        : "border-border/60 hover:bg-muted"
                    }`}
                  >
                    {n}
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              {Array(partes)
                .fill(null)
                .map((_, i) => (
                  <div
                    key={i}
                    className="flex items-center gap-3 rounded-xl border border-border/60 bg-card px-4 py-3"
                  >
                    <UtensilsCrossed className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
                    <span className="min-w-[80px] text-sm font-medium">Comensal {i + 1}</span>
                    {mode === "igual" ? (
                      <span className="ml-auto text-lg font-bold tabular-nums text-primary">
                        {pesos(porParte)}
                      </span>
                    ) : (
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={customAmounts[i]}
                        onChange={(e) => {
                          const next = [...customAmounts];
                          next[i] = e.target.value;
                          setCustomAmounts(next);
                        }}
                        placeholder="0"
                        className="ml-auto w-32 rounded-xl border border-border/60 bg-card px-3 py-1.5 text-right text-sm tabular-nums focus:outline-none focus:ring-1 focus:ring-primary/40"
                      />
                    )}
                  </div>
                ))}
            </div>

            {mode === "custom" && (
              <div
                className={`flex items-center justify-between rounded-xl border px-4 py-3 ${
                  Math.abs(diferencia) <= 1
                    ? "border-green-500/30 bg-green-500/10"
                    : "border-amber-500/30 bg-amber-500/10"
                }`}
              >
                <span className="text-sm">Diferencia sin asignar</span>
                <span
                  className={`text-sm font-bold tabular-nums ${
                    Math.abs(diferencia) <= 1 ? "text-green-400" : "text-amber-400"
                  }`}
                >
                  {pesos(Math.abs(diferencia))}
                </span>
              </div>
            )}

            <Button
              className="h-14 w-full text-base font-semibold"
              disabled={procesando || total <= 0 || (mode === "custom" && Math.abs(diferencia) > 1)}
              onClick={() => void handleConfirmar()}
            >
              <CheckCircle2 className="mr-2 h-5 w-5" />
              {procesando ? "Guardando…" : "Confirmar división"}
            </Button>
          </div>
        )}
      </ClinicalPageShell>
    </div>
  );
}
